import { useApplicantAPI, fetcher, InferApplicantAPI } from "@certn/api";
import {
  Container,
  Loader,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@certn/cascadia-react";
import { BasicButton } from "@certn/cascadia";
import { useEffect, useState, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useResolvedPath,
} from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { createEndsWithTabIdMapper } from "@certn/utils";

import { PaymentProfilesTab } from "./PaymentProfilesTab";
import { AddPaymentProfileModal } from "./components/AddPaymentProfileModal";
import { AppTitle } from "../../../components/AppTitle";
import { useDocumentTitle } from "../../../hooks/api/useDocumentTitle";
import { components } from "packages/api/generated-schemas/applicant-portal";

export function PaymentDetailsPage() {
  const { t } = useTranslation();
  useDocumentTitle(t("Payment details — Certn"));
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const url = useResolvedPath("").pathname;
  const [showNewPaymentModal, setShowNewPaymentModal] = useState(false);

  // Map pathnames to static CSS-safe tab IDs for React 19 compatibility
  const mapSelectedTabId = createEndsWithTabIdMapper(
    [
      { suffix: "/archived", id: "archived-tab" },
      { suffix: "/failed", id: "failed-tab" },
    ],
    "active-tab"
  );

  const selectedTabId = mapSelectedTabId(pathname);

  const { caseManagementEnableApplicantPayer } = useFlags();
  const [allPaymentProfiles, setAllPaymentProfiles] = useState<
    components["schemas"]["PaymentProfileList"][]
  >([]);
  const [isLoadingAll, setIsLoadingAll] = useState(true);

  // Redirect if feature flag is disabled
  useEffect(() => {
    if (!caseManagementEnableApplicantPayer) {
      navigate("/", { replace: true });
    }
  }, [caseManagementEnableApplicantPayer, navigate]);

  const paymentProfilesUrl = "/api/internal/applicant/me/payment-profiles/";
  const { data, mutate } = useApplicantAPI(paymentProfilesUrl);
  const [cachedPaymentProfileCount, setCachedPaymentProfileCount] = useState<
    number | undefined
  >();

  useEffect(() => {
    if (typeof data?.count === "number") {
      setCachedPaymentProfileCount(data.count);
    }
  }, [data?.count]);

  type PaymentProfilesResponse = InferApplicantAPI<typeof paymentProfilesUrl>;

  // Fetch all paginated pages and combine into a single list
  const fetchAllPages = useCallback(async (responseData: typeof data) => {
    if (!responseData) return;

    setIsLoadingAll(true);
    const allResults: components["schemas"]["PaymentProfileList"][] = [
      ...(responseData.results || []),
    ];
    let nextUrl = responseData.next;

    while (nextUrl) {
      try {
        const nextPageData =
          await fetcher.request<PaymentProfilesResponse>(nextUrl);
        if (nextPageData) {
          allResults.push(...(nextPageData.results || []));
          nextUrl = nextPageData.next || null;
        } else {
          break;
        }
      } catch {
        // Silently fail - user will see existing profiles
        break;
      }
    }

    setAllPaymentProfiles(allResults);
    setIsLoadingAll(false);
  }, []);

  // Auto-load all pages when data changes
  useEffect(() => {
    fetchAllPages(data);
  }, [data, fetchAllPages]);

  // Wrapper for mutate that ensures all paginated pages are fetched
  const mutateAndRefetch = useCallback(async () => {
    await mutate();
    // Manually fetch first page since mutate() may not update data reference immediately
    const firstPage =
      await fetcher.request<PaymentProfilesResponse>(paymentProfilesUrl);
    if (firstPage) {
      await fetchAllPages(firstPage);
    }
  }, [mutate, fetchAllPages, paymentProfilesUrl]);

  const archivedUrl = `${url}/archived`;
  const failedUrl = `${url}/failed`;

  const handleOpenAddPaymentProfileModal = () => {
    setShowNewPaymentModal(true);
  };

  const handleClose = () => {
    setShowNewPaymentModal(false);
    void mutateAndRefetch();
  };

  // Don't render anything if feature flag is disabled (they'll be redirected)
  if (!caseManagementEnableApplicantPayer) {
    return <Loader />;
  }

  // Refetching the list sets isLoadingAll and can drop `data.results`. Either
  // one used to replace this page with a loader and unmount the open Stripe
  // dialog. Keep the dialog mounted until the user closes it. FR-1442.
  if ((!data?.results || isLoadingAll) && !showNewPaymentModal) {
    return <Loader />;
  }

  return (
    <Container>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "var(--space-4)",
        }}
      >
        <AppTitle title={t("Payment details")} />
        <BasicButton
          testID="add-new-payment-profile-button"
          onPress={() => handleOpenAddPaymentProfileModal()}
        >
          {t("New payment profile")}
        </BasicButton>
      </div>
      <Tabs
        gap="space-3"
        selectedKey={selectedTabId}
        testID="payment-details-tabs"
      >
        <TabList aria-label={t("Payment profile status")}>
          <Tab id="active-tab" href={url} testID="active-payment-profiles-tab">
            <Trans>Active</Trans>
          </Tab>
          <Tab
            id="archived-tab"
            href={archivedUrl}
            testID="archived-payment-profiles-tab"
          >
            <Trans>Archived</Trans>
          </Tab>
          <Tab
            id="failed-tab"
            href={failedUrl}
            testID="failed-payment-profiles-tab"
          >
            <Trans>Failed</Trans>
          </Tab>
        </TabList>
        <TabPanel id={selectedTabId}>
          <Routes>
            <Route
              path=""
              element={
                <PaymentProfilesTab
                  paymentProfiles={allPaymentProfiles}
                  statusFilter="ACTIVE"
                  mutate={mutateAndRefetch}
                />
              }
            />
            <Route
              path="/archived"
              element={
                <PaymentProfilesTab
                  paymentProfiles={allPaymentProfiles}
                  statusFilter="INACTIVE"
                  mutate={mutateAndRefetch}
                />
              }
            />
            <Route
              path="/failed"
              element={
                <PaymentProfilesTab
                  paymentProfiles={allPaymentProfiles}
                  statusFilter="FAILED"
                  mutate={mutateAndRefetch}
                />
              }
            />
          </Routes>
        </TabPanel>
      </Tabs>
      {showNewPaymentModal && (
        <AddPaymentProfileModal
          handleClose={() => handleClose()}
          initialPaymentProfileCount={
            typeof data?.count === "number"
              ? data.count
              : cachedPaymentProfileCount ?? allPaymentProfiles.length
          }
        />
      )}
    </Container>
  );
}
